import React, { useState, useEffect, useCallback } from 'react';
import { Users, Shield, Eye, Download, Search, Filter, ChevronDown, X, Check, AlertTriangle, Clock, UserCheck, UserX, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_SECRET || '';

// Helper to get admin headers
const getAdminHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Admin-Password': ADMIN_PASSWORD
});

// Role and Status options
const ROLES = [
  { value: 'viewer', label: 'Viewer', color: 'gray' },
  { value: 'investor', label: 'Investor', color: 'green' },
  { value: 'director', label: 'Director', color: 'blue' },
  { value: 'producer', label: 'Producer', color: 'purple' },
  { value: 'cast', label: 'Cast', color: 'pink' },
  { value: 'crew', label: 'Crew', color: 'orange' },
  { value: 'admin', label: 'Admin', color: 'red' }
];

const STATUSES = [
  { value: 'active', label: 'Active', color: 'green', icon: UserCheck },
  { value: 'pending_verification', label: 'Pending', color: 'yellow', icon: Clock },
  { value: 'revoked', label: 'Revoked', color: 'red', icon: UserX }
];

// Helper to get role/status styling
const getRoleStyle = (role) => {
  const r = ROLES.find(r => r.value === role);
  return r ? `bg-${r.color}-500/20 text-${r.color}-400 border-${r.color}-500/30` : 'bg-gray-500/20 text-swp-white-ghost';
};

const getStatusStyle = (status) => {
  const s = STATUSES.find(s => s.value === status);
  return s ? `bg-${s.color}-500/20 text-${s.color}-400` : 'bg-gray-500/20 text-swp-white-ghost';
};

const DOC_TYPES = [
  { id: 'financials', label: 'Financials' },
  { id: 'scripts', label: 'Scripts' },
  { id: 'deck', label: 'Deck' },
  { id: 'updates', label: 'Updates' },
];

// ============ USER CARD ============
const UserCard = ({ user, projects, onUpdate, onViewDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    role: user.role,
    status: user.status,
    has_all_projects_access: user.has_all_projects_access || false,
    project_access: user.project_access || []
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/studio-portal/console/users/${user.id}`, {
        method: 'PUT',
        headers: getAdminHeaders(),
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        toast.success('User updated successfully');
        setEditMode(false);
        onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm(`Revoke access for ${user.full_name}?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/studio-portal/console/users/${user.id}`, {
        method: 'DELETE',
        headers: getAdminHeaders()
      });

      if (response.ok) {
        toast.success('Access revoked');
        onUpdate();
      } else {
        toast.error('Failed to revoke access');
      }
    } catch (err) {
      toast.error('Network error');
    }
  };

  const toggleProject = (projectId) => {
    setEditData(prev => {
      const existing = prev.project_access.find(pa => pa.project_id === projectId);
      if (existing) {
        return { ...prev, project_access: prev.project_access.filter(pa => pa.project_id !== projectId) };
      } else {
        return { ...prev, project_access: [...prev.project_access, { project_id: projectId, docs: ['deck', 'updates'] }] };
      }
    });
  };

  const toggleDoc = (projectId, docType) => {
    setEditData(prev => ({
      ...prev,
      project_access: prev.project_access.map(pa => {
        if (pa.project_id !== projectId) return pa;
        const hasDocs = pa.docs.includes(docType);
        return { ...pa, docs: hasDocs ? pa.docs.filter(d => d !== docType) : [...pa.docs, docType] };
      })
    }));
  };

  return (
    <div className="bg-swp-surface border border-swp-border rounded-swp overflow-hidden" data-testid={`user-card-${user.id}`}>
      {/* Header Row */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-swp-black/20"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-sm bg-swp-ice/15 flex items-center justify-center text-swp-ice font-bold">
            {user.full_name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <h4 className="text-white font-medium">{user.full_name}</h4>
            <p className="text-swp-white-ghost/70 text-sm">{user.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded text-xs uppercase ${
            user.role === 'admin' ? 'bg-red-500/20 text-red-400' :
            user.role === 'investor' ? 'bg-green-500/20 text-green-400' :
            user.role === 'director' ? 'bg-blue-500/20 text-blue-400' :
            user.role === 'producer' ? 'bg-purple-500/20 text-purple-400' :
            'bg-gray-500/20 text-swp-white-ghost'
          }`}>
            {user.role}
          </span>
          <span className={`px-2 py-1 rounded text-xs uppercase ${
            user.status === 'active' ? 'bg-green-500/20 text-green-400' :
            user.status === 'pending_verification' ? 'bg-yellow-500/20 text-yellow-400' :
            'bg-red-500/20 text-red-400'
          }`}>
            {user.status === 'pending_verification' ? 'Pending' : user.status}
          </span>
          <ChevronDown size={18} className={`text-swp-white-ghost/70 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-swp-border p-4 space-y-4">
          {/* Info Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-swp-white-ghost/70 block">Company</span>
              <span className="text-white">{user.company || '-'}</span>
            </div>
            <div>
              <span className="text-swp-white-ghost/70 block">Joined</span>
              <span className="text-white">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</span>
            </div>
            <div>
              <span className="text-swp-white-ghost/70 block">Last Login</span>
              <span className="text-white">{user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}</span>
            </div>
            <div>
              <span className="text-swp-white-ghost/70 block">Project Access</span>
              <span className="text-white">
                {user.has_all_projects_access ? 'All Projects' : `${user.project_access?.length || user.project_permissions?.length || 0} projects`}
              </span>
            </div>
          </div>

          {/* Edit Mode */}
          {editMode ? (
            <div className="space-y-4 pt-4 border-t border-swp-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Role Select */}
                <div>
                  <label className="block text-swp-white-ghost text-xs uppercase mb-2">Role</label>
                  <select
                    value={editData.role}
                    onChange={(e) => setEditData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full bg-swp-black border border-swp-border rounded-swp px-3 py-2 text-white text-sm focus:border-swp-ice focus:outline-none"
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>

                {/* Status Select */}
                <div>
                  <label className="block text-swp-white-ghost text-xs uppercase mb-2">Status</label>
                  <select
                    value={editData.status}
                    onChange={(e) => setEditData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full bg-swp-black border border-swp-border rounded-swp px-3 py-2 text-white text-sm focus:border-swp-ice focus:outline-none"
                  >
                    {STATUSES.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* All Projects Access Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editData.has_all_projects_access}
                    onChange={(e) => setEditData(prev => ({ ...prev, has_all_projects_access: e.target.checked }))}
                    className="rounded border-gray-600 bg-swp-black text-swp-ice focus:ring-swp-ice"
                  />
                  <span className="text-swp-white-dim text-sm">Grant access to all projects</span>
                </label>
              </div>

              {/* Project-Scoped Access with Doc Permissions */}
              {!editData.has_all_projects_access && projects.length > 0 && (
                <div>
                  <label className="block text-swp-white-ghost text-xs uppercase mb-2">Project Access & Document Permissions</label>
                  <div className="space-y-3">
                    {projects.map(project => {
                      const pa = editData.project_access.find(a => a.project_id === project.id);
                      const isSelected = !!pa;
                      return (
                        <div key={project.id} className={`rounded-sm border transition-all ${isSelected ? 'bg-swp-ice/5 border-swp-ice/30' : 'bg-swp-black border-swp-border'}`}>
                          <button
                            type="button"
                            onClick={() => toggleProject(project.id)}
                            className={`w-full px-3 py-2 text-left text-xs flex items-center justify-between transition-all ${isSelected ? 'text-swp-ice' : 'text-swp-white-ghost hover:text-white'}`}
                          >
                            <span className="font-medium">{project.title}</span>
                            <span className="text-xs opacity-60">{isSelected ? 'Enabled' : 'Off'}</span>
                          </button>
                          {isSelected && (
                            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                              {DOC_TYPES.map(dt => (
                                <button
                                  key={dt.id}
                                  type="button"
                                  onClick={() => toggleDoc(project.id, dt.id)}
                                  className={`px-2.5 py-1 rounded-sm text-xs transition-all ${pa.docs.includes(dt.id) ? 'bg-swp-ice text-white' : 'bg-swp-black border border-swp-border text-swp-white-ghost/50 hover:border-gray-500'}`}
                                >
                                  {dt.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Save/Cancel Buttons */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-swp-ice hover:bg-swp-ice disabled:bg-swp-muted text-white rounded-swp text-sm transition-all"
                >
                  {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditMode(false);
                    setEditData({
                      role: user.role,
                      status: user.status,
                      has_all_projects_access: user.has_all_projects_access || false,
                      project_access: user.project_access || []
                    });
                  }}
                  className="px-4 py-2 border border-swp-border text-swp-white-ghost hover:text-swp-white rounded-swp text-sm transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Action Buttons */
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 px-4 py-2 bg-swp-ice/10 hover:bg-swp-ice/15 text-swp-ice rounded-swp text-sm transition-all"
              >
                <Shield size={14} />
                Edit Access
              </button>
              {user.status !== 'revoked' && user.role !== 'admin' && (
                <button
                  onClick={handleRevoke}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-swp text-sm transition-all"
                >
                  <UserX size={14} />
                  Revoke Access
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============ STATS CARD ============
const StatsCard = ({ title, value, subtitle, icon: Icon, color = 'blue' }) => (
  <div className="bg-swp-surface border border-swp-border rounded-swp p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-swp-white-ghost/70 text-sm">{title}</p>
        <p className="text-3xl font-bold text-white mt-1">{value}</p>
        {subtitle && <p className="text-swp-white-ghost/70 text-xs mt-1">{subtitle}</p>}
      </div>
      <div className={`p-2 rounded-swp bg-${color}-500/10`}>
        <Icon size={20} className={`text-${color}-400`} />
      </div>
    </div>
  </div>
);

// ============ MAIN COMPONENT ============
const AdminStudioPortalTab = () => {
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('users');

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      // Fetch users, stats, and projects in parallel
      const [usersRes, statsRes, projectsRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/studio-portal/console/users`, {
          headers: getAdminHeaders()
        }),
        fetch(`${API_URL}/api/admin/studio-portal/console/stats`, {
          headers: getAdminHeaders()
        }),
        fetch(`${API_URL}/api/films`),
        fetch(`${API_URL}/api/admin/studio-portal/console/audit-logs?limit=50`, {
          headers: getAdminHeaders()
        })
      ]);

      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users || []);
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (projectsRes.ok) {
        const data = await projectsRes.json();
        // Filter to only show films/apps with studio_access_enabled
        const allProjects = data.films || data || [];
        const studioProjects = allProjects.filter(p => p.studio_access_enabled === true);
        setProjects(studioProjects);
      }

      if (logsRes.ok) {
        const data = await logsRes.json();
        setAuditLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = !searchTerm || 
      user.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus = !filterStatus || user.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw size={32} className="text-swp-ice animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-studio-portal-tab">
      {/* Header - Removed since it's now wrapped by AdminStudioTab */}

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatsCard 
            title="Total Users" 
            value={stats.users?.total || 0} 
            icon={Users} 
            color="blue" 
          />
          <StatsCard 
            title="Active Users" 
            value={stats.users?.active || 0}
            subtitle={`${stats.users?.pending || 0} pending`}
            icon={UserCheck} 
            color="green" 
          />
          <StatsCard 
            title="Downloads" 
            value={stats.activity?.total_downloads || 0} 
            icon={Download} 
            color="purple" 
          />
          <StatsCard 
            title="Revoked" 
            value={stats.users?.revoked || 0} 
            icon={UserX} 
            color="red" 
          />
        </div>
      )}

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-swp-border">
        {['users', 'audit'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-4 py-2 font-mono text-sm uppercase tracking-widest border-b-2 transition-all ${
              activeSubTab === tab
                ? 'text-swp-ice border-swp-ice'
                : 'text-swp-white-ghost/70 border-transparent hover:text-swp-white-dim'
            }`}
          >
            {tab === 'users' ? 'User Management' : 'Audit Log'}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeSubTab === 'users' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-swp-white-ghost/70" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-10 pr-4 py-2 bg-swp-surface border border-swp-border rounded-swp text-white text-sm focus:border-swp-ice focus:outline-none"
                data-testid="user-search-input"
              />
            </div>

            {/* Role Filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-2 bg-swp-surface border border-swp-border rounded-swp text-swp-white-dim text-sm focus:border-swp-ice focus:outline-none"
            >
              <option value="">All Roles</option>
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 bg-swp-surface border border-swp-border rounded-swp text-swp-white-dim text-sm focus:border-swp-ice focus:outline-none"
            >
              <option value="">All Statuses</option>
              {STATUSES.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>

            {/* Clear Filters */}
            {(searchTerm || filterRole || filterStatus) && (
              <button
                onClick={() => { setSearchTerm(''); setFilterRole(''); setFilterStatus(''); }}
                className="flex items-center gap-1 px-3 py-2 text-swp-white-ghost hover:text-swp-white text-sm"
              >
                <X size={14} />
                Clear
              </button>
            )}
          </div>

          {/* User Count */}
          <p className="text-swp-white-ghost/70 text-sm">
            Showing {filteredUsers.length} of {users.length} users
          </p>

          {/* User List */}
          <div className="space-y-3">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-swp-white-ghost/70">
                <Users size={48} className="mx-auto mb-4 opacity-50" />
                <p>No users found</p>
              </div>
            ) : (
              filteredUsers.map(user => (
                <UserCard
                  key={user.id}
                  user={user}
                  projects={projects}
                  onUpdate={fetchData}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Audit Log Tab */}
      {activeSubTab === 'audit' && (
        <div className="space-y-4">
          <div className="bg-swp-surface border border-swp-border rounded-swp overflow-hidden">
            <table className="w-full">
              <thead className="bg-swp-deep/50">
                <tr>
                  <th className="text-left px-4 py-3 text-swp-white-ghost text-xs uppercase">Time</th>
                  <th className="text-left px-4 py-3 text-swp-white-ghost text-xs uppercase">Event</th>
                  <th className="text-left px-4 py-3 text-swp-white-ghost text-xs uppercase">User</th>
                  <th className="text-left px-4 py-3 text-swp-white-ghost text-xs uppercase">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-swp-white-ghost/70">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log.id || log.timestamp} className="hover:bg-swp-black/20">
                      <td className="px-4 py-3 text-swp-white-ghost/70 text-sm">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs uppercase ${
                          log.event_type?.includes('download') ? 'bg-purple-500/20 text-purple-400' :
                          log.event_type?.includes('login') ? 'bg-green-500/20 text-green-400' :
                          log.event_type?.includes('revoked') ? 'bg-red-500/20 text-red-400' :
                          'bg-gray-500/20 text-swp-white-ghost'
                        }`}>
                          {log.event_type?.replace(/_/g, ' ') || 'unknown'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white text-sm">
                        {log.user_email || '-'}
                      </td>
                      <td className="px-4 py-3 text-swp-white-ghost text-sm max-w-xs">
                        {log.details ? (
                          <span className="block truncate">
                            {typeof log.details === 'object' 
                              ? Object.entries(log.details)
                                  .filter(([k, v]) => v && k !== '_id')
                                  .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
                                  .join(', ')
                              : log.details}
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStudioPortalTab;
